/**
 * Cache Management Utility
 * In-memory cache with TTL (Time To Live) support
 * Used to reduce redundant API calls and improve performance
 */

class Cache {
  constructor(defaultTTL = 5 * 60 * 1000) { // 5 minutes default
    this.store = new Map();
    this.defaultTTL = defaultTTL;
    this.enabled = process.env.REACT_APP_ENABLE_CACHING !== 'false';
  }

  /**
   * Generate cache key from various inputs
   * @param {string|Array} key - Cache key or array of key parts
   * @returns {string} - Normalized cache key
   */
  generateKey(key) {
    if (Array.isArray(key)) {
      return key.join(':');
    }
    return String(key);
  }

  /**
   * Get value from cache
   * @param {string|Array} key - Cache key
   * @returns {any|null} - Cached value or null if expired/missing
   */
  get(key) {
    if (!this.enabled) return null;

    const normalizedKey = this.generateKey(key);
    const cached = this.store.get(normalizedKey);

    if (!cached) {
      return null;
    }

    // Check if expired
    if (Date.now() > cached.expiresAt) {
      this.store.delete(normalizedKey);
      return null;
    }

    // Update access time for LRU tracking
    cached.lastAccessed = Date.now();

    return cached.value;
  }

  /**
   * Set value in cache
   * @param {string|Array} key - Cache key
   * @param {any} value - Value to cache
   * @param {number} ttl - Time to live in milliseconds (optional)
   */
  set(key, value, ttl) {
    if (!this.enabled) return;

    const normalizedKey = this.generateKey(key);
    const timeToLive = ttl || this.defaultTTL;

    this.store.set(normalizedKey, {
      value,
      expiresAt: Date.now() + timeToLive,
      createdAt: Date.now(),
      lastAccessed: Date.now(),
    });

    // Cleanup old entries if cache gets too large (simple LRU)
    if (this.store.size > 1000) {
      this.cleanup();
    }
  }

  /**
   * Check if key exists and is valid (not expired)
   * @param {string|Array} key - Cache key
   * @returns {boolean}
   */
  has(key) {
    const value = this.get(key);
    return value !== null;
  }

  /**
   * Invalidate/delete specific cache entry
   * @param {string|Array} key - Cache key
   */
  invalidate(key) {
    const normalizedKey = this.generateKey(key);
    this.store.delete(normalizedKey);
  }

  /**
   * Invalidate all cache entries matching a pattern
   * @param {string} pattern - Pattern to match (supports wildcards with *)
   */
  invalidatePattern(pattern) {
    const regex = new RegExp(pattern.replace(/\*/g, '.*'));

    for (const key of this.store.keys()) {
      if (regex.test(key)) {
        this.store.delete(key);
      }
    }
  }

  /**
   * Clear all cache entries
   */
  clear() {
    this.store.clear();
  }

  /**
   * Get cache statistics
   * @returns {Object} - Cache stats
   */
  getStats() {
    const now = Date.now();
    let validEntries = 0;
    let expiredEntries = 0;

    for (const [key, entry] of this.store.entries()) {
      if (now > entry.expiresAt) {
        expiredEntries++;
      } else {
        validEntries++;
      }
    }

    return {
      total: this.store.size,
      valid: validEntries,
      expired: expiredEntries,
      enabled: this.enabled,
    };
  }

  /**
   * Cleanup expired entries and enforce size limit (LRU)
   */
  cleanup() {
    const now = Date.now();
    const entries = Array.from(this.store.entries());

    // Remove expired entries
    for (const [key, entry] of entries) {
      if (now > entry.expiresAt) {
        this.store.delete(key);
      }
    }

    // If still too large, remove least recently accessed
    if (this.store.size > 1000) {
      const sortedEntries = Array.from(this.store.entries())
        .sort(([, a], [, b]) => a.lastAccessed - b.lastAccessed);

      // Remove oldest 10%
      const toRemove = Math.floor(sortedEntries.length * 0.1);
      for (let i = 0; i < toRemove; i++) {
        this.store.delete(sortedEntries[i][0]);
      }
    }
  }

  /**
   * Get or set pattern (memoization helper)
   * If key exists, return cached value
   * If not, execute fetchFn and cache the result
   *
   * @param {string|Array} key - Cache key
   * @param {Function} fetchFn - Function to fetch data if not cached
   * @param {number} ttl - Time to live (optional)
   * @returns {Promise<any>}
   */
  async getOrSet(key, fetchFn, ttl) {
    const cached = this.get(key);

    if (cached !== null) {
      return cached;
    }

    const value = await fetchFn();
    this.set(key, value, ttl);
    return value;
  }

  /**
   * Enable/disable caching globally
   * @param {boolean} enabled
   */
  setEnabled(enabled) {
    this.enabled = enabled;
    if (!enabled) {
      this.clear();
    }
  }
}

// Create singleton instance
const apiCache = new Cache(
  parseInt(process.env.REACT_APP_CACHE_TTL) || 5 * 60 * 1000
);

// Auto-cleanup every 5 minutes
setInterval(() => {
  apiCache.cleanup();
}, 5 * 60 * 1000);

// Log cache stats in development mode
if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEBUG_MODE === 'true') {
  setInterval(() => {
    console.log('[Cache Stats]', apiCache.getStats());
  }, 60 * 1000); // Every minute
}

export default apiCache;
export { Cache };
